import { Star } from 'lucide-react';

import { Avatar } from '../../../shared/ui';
import type { PublicAsset } from '../../../shared/lib';
import type { PublicTestimonial } from '../publicApi';

export interface TestimonialCardProps {
  testimonial: PublicTestimonial;
  photo: PublicAsset | undefined;
}

const MAX_RATING = 5;

/**
 * Quote, author, rating — only when one exists (FR-TEST-03). A default of
 * five stars would be fabricated data, so the absence of `rating` renders no
 * star row at all rather than a guess.
 */
export function TestimonialCard({ testimonial, photo }: TestimonialCardProps) {
  return (
    <figure className="hs-card flex flex-col gap-4 rounded-[var(--hs-card-radius)] p-[var(--hs-card-padding)]">
      {testimonial.rating !== null ? (
        <div
          className="flex gap-0.5 text-accent"
          role="img"
          aria-label={`${testimonial.rating} / ${MAX_RATING}`}
        >
          {Array.from({ length: MAX_RATING }, (_, i) => (
            <Star
              key={i}
              className="size-4"
              aria-hidden="true"
              fill={i < (testimonial.rating ?? 0) ? 'currentColor' : 'none'}
            />
          ))}
        </div>
      ) : null}

      <blockquote className="line-clamp-6 text-text-muted">“{testimonial.review}”</blockquote>

      <figcaption className="mt-auto flex items-center gap-3">
        <Avatar name={testimonial.author_name} src={photo?.url} />
        <div>
          <p className="text-sm font-medium text-text">{testimonial.author_name}</p>
          {testimonial.position || testimonial.company ? (
            <p className="hs-meta text-text-subtle">
              {[testimonial.position, testimonial.company].filter(Boolean).join(', ')}
            </p>
          ) : null}
        </div>
      </figcaption>
    </figure>
  );
}
